import { Link, useLocation, useMatchRoute } from "@tanstack/react-router";
import { Goal, Layers, type LucideProps, Rocket, Settings } from "lucide-react";
import React from "react";
import { NavFooter } from "~/components/app-shell/nav-footer";
import { NavHeader } from "~/components/app-shell/sidebar-header";
import { FeedbackButton } from "~/components/feedback-button/feedback-button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "~/components/ui/sidebar";
import { useFeatureFlag } from "~/instrumentation/feature-flags";
import type { AppRoute } from "~/main";

type Icon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

type MenuItem = {
  title: string;
  url: string;
  icon: Icon;
  match?: AppRoute;
  disabled?: boolean;
};

type SidebarSection = {
  title?: string;
  items: MenuItem[];
  disabled?: boolean;
};

const SecondaryMenuItems: MenuItem[] = [
  {
    title: "Settings",
    icon: Settings,
    url: "/settings",
    match: "/settings",
  },
];

export function AppSidebar() {
  const { pathname } = useLocation();
  const { open } = useSidebar();

  const matchRoute = useMatchRoute();

  const isQuickstartEnabled = useFeatureFlag("quickstart");

  const MenuSections: SidebarSection[] = [
    {
      disabled: !isQuickstartEnabled,
      items: [
        {
          title: "Get Started!",
          icon: Rocket,
          url: "/",
        },
      ],
    },
    {
      items: [
        {
          title: "Variants",
          icon: Layers,
          url: "/pages",
          match: "/pages/$pageId",
        },
        {
          title: "Goals",
          icon: isQuickstartEnabled ? Goal : Rocket,
          url: "/conversions",
        },
      ],
    },
  ];

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <NavHeader />
      </SidebarHeader>
      <SidebarContent>
        {MenuSections.map((section, idx) =>
          section.disabled ? null : (
            <SidebarGroup key={section.title ?? idx}>
              {section.title && (
                <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) =>
                    item.disabled ? null : (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={
                            item.url === pathname ||
                            (item?.match &&
                              !!matchRoute({
                                to: item.match as string,
                                fuzzy: true,
                              }))
                          }
                          tooltip={{
                            children: item.title,
                            hidden: open,
                          }}
                        >
                          <Link to={item.url}>
                            <item.icon />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ),
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ),
        )}
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              {SecondaryMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    tooltip={{ children: item.title, hidden: open }}
                    isActive={
                      item.url === pathname ||
                      (item?.match &&
                        !!matchRoute({
                          to: item.match as string,
                          fuzzy: true,
                        }))
                    }
                  >
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              <SidebarMenuItem>
                <FeedbackButton showTooltip={!open} />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <NavFooter />
      </SidebarFooter>
    </Sidebar>
  );
}
